import type { RuleSetting } from '../lib/api.ts'
import { Checkbox } from './Checkbox.tsx'

const SECTION_LABEL: Readonly<Record<string, string>> = {
  other: 'Operação',
  label: 'Rótulo',
}

const SEVERITY_LABEL: Readonly<Record<RuleSetting['severity'], string>> = {
  error: 'erro — bloqueia a liberação',
  reminder: 'lembrete',
}

/** "disparou 422 vezes nos últimos 30 dias", and a plain no when it did not. */
function firedLabel(fired: number, windowDays: number): string {
  const window = `nos últimos ${windowDays} dias`
  if (fired === 0) return `não disparou ${window}`
  return `disparou ${fired.toLocaleString('pt-BR')} ${fired === 1 ? 'vez' : 'vezes'} ${window}`
}

/**
 * An account's operational rules, by section, each with its switch.
 *
 * It owns no state: which rules are off is the caller's edit, saved with
 * whatever else that screen saves.
 *
 * Every row carries what it takes to decide: what the rule checks, how loudly
 * it speaks, and how often it fired here. A rule that never fired and one that
 * fires on every other requisição are different decisions, and the title alone
 * cannot tell them apart.
 */
export function RuleList({
  rules,
  disabled,
  windowDays,
  onToggle,
}: {
  rules: readonly RuleSetting[]
  /** The ids switched off in the edit under way. */
  disabled: readonly string[]
  windowDays: number
  onToggle: (id: string, on: boolean) => void
}) {
  const sections = [...new Set(rules.map((rule) => rule.section))]

  return (
    <div className="flex flex-col gap-5">
      {sections.map((section) => (
        <div key={section} className="flex flex-col gap-3">
          <span className="text-tSmall text-text-weak uppercase tracking-wide">
            {SECTION_LABEL[section] ?? section}
          </span>
          {rules
            .filter((rule) => rule.section === section)
            .map((rule) => (
              <label key={rule.id} className="flex items-start gap-3">
                <Checkbox
                  checked={!disabled.includes(rule.id)}
                  onChange={(event) => onToggle(rule.id, event.target.checked)}
                />
                <span className="flex flex-col gap-0.5">
                  <span className="text-tP text-text-strong">{rule.title}</span>
                  <span className="text-tSmall text-text-weak max-w-[80ch]">
                    {rule.description}
                  </span>
                  <span className="text-tSmall text-text-weak">
                    {SEVERITY_LABEL[rule.severity]} · {firedLabel(rule.fired, windowDays)}
                  </span>
                </span>
              </label>
            ))}
        </div>
      ))}
    </div>
  )
}
